import { bind, trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';

// Helpers
import { getOverride } from '@confluence/meta';
import { applyThemeInsideIframe, setupThemeInsideIframe } from '@confluence/theming-utils';

// Legacy code brought over from Confluence
import { debugTime, debugTimeEnd } from './performance-session';
import { configureToolbar } from './toolbar-configuration-manager';
import AtlassianEditorContent from './atlassian-editor-content';

// Externals and stuff
import window from 'window-or-global';
import { exportAjsRteAsGlobal } from '../global-objects/ajs-rte';

/**
 * Initialises the editor
 */
const defaultSettings = {
	atlassian: true,
	keep_values: true,
	convert_urls: true,
	relative_urls: false,
	// CONFDEV-1555 - Required for IE and SSL to ensure complete URLs for inline style urls. Otherwise IE displays security warnings.
	remove_script_host: false,
	button_tile_map: true,
	gecko_spellcheck: true,
	apply_source_formatting: false, //we don't want new lines between html elements when serialized and sent to the backend
	list_outdent_on_enter: true,

	// table settings
	visual: false,
	confluence_table_style: 'confluenceTable',
	confluence_table_cell_style: 'confluenceTd',
	confluence_table_heading_style: 'confluenceTh',
	confluence_table_default_rows: 4,
	confluence_table_default_cols: 3,
	confluence_table_default_heading: true,

	// output settings
	cleanup: true,
	/**
	 * This is to ensure that any empty elements that require padding are padded with an &nbsp; (say PRE, P, DIV etc.)
	 * These spaces are an editor concern and should not be persisted to our storage.
	 */
	cleanup_on_startup: true,

	/**
	 * This must be false since browsers depend on broken list markup to provide working bullet functionality.
	 * We fix up the broken list markup on the server side before we save it so its not necessary here anyway.
	 */
	fix_list_elements: false,
	fix_table_elements: true,

	// Theoretically, most elements are allowed to have a wysiwyg parameter set.  See ExternallyDefinedConverter
	valid_elements:
		'@[id|class|style|title|wysiwyg|dir<ltr?rtl|lang|xml::lang|onclick|ondblclick|onmousedown|onmouseup|onmouseover|onmousemove|onmouseout|onkeypress|onkeydown|onkeyup],' +
		// We add extra link attributes to help conversion to wiki markup
		'a[*],' +
		'strong/b,em/i,s,u,' +
		// User paragraphs indicate a blank line.  See DefaultWysiwygConverter.isUserNewline
		'#p[align|user],' +
		'-ol[type|compact],-ul[type|compact],li,br,' +
		// The ImageConverter uses an imagetext attribute
		'img[imagetext|longdesc|usemap|src|border|alt=|title|hspace|vspace|width|height|align],' +
		'-sub,-sup,' +
		// the markup tag is used to distinguish bq. markup from {quote}s.  See BlockQuoteConverter.java
		'-blockquote[cite|markup],' +
		'-table[*],' +
		'-tr[rowspan|width|height|align|valign|bgcolor|background|bordercolor],tbody,thead,tfoot,' +
		'#td[colspan|rowspan|width|height|align|valign|bgcolor|background|bordercolor|scope],' +
		'#th[colspan|rowspan|width|height|align|valign|scope],caption,' +
		'div[*],' +
		/**
		 * CONFDEV-48486
		 * In the past, pattern was #span[*] which means if SPAN is empty, we appended &nbsp; into it
		 * Prefix # tells tinyMCE to check if a SPAN is empty
		 * Now we change from &nbsp; to BR (WD-567), SPAN with BR inside make no sense
		 * We remove # so tinyMCE won't check for emptiness of a SPAN
		 */
		'span[*],' +
		'-code,#pre[*],address,-h1,-h2,-h3,-h4,-h5,-h6,hr[size|noshade],' +
		'-font[face|size|color],dd,dl,dt,cite,abbr,acronym,del[datetime|cite],ins[datetime|cite],object[classid|width|height|codebase|*],param[name|value],' +
		'embed[type|width|height|src|*],map[name],area[shape|coords|href|alt|target],bdo,button,col[align|char|charoff|span|valign|width],' +
		'colgroup[align|char|charoff|span|valign|width],dfn,fieldset,form[action|accept|accept-charset|enctype|method],' +
		'input[accept|alt|checked|disabled|maxlength|name|readonly|size|src|type|value],kbd,label[for],legend,noscript,' +
		'optgroup[label|disabled],option[disabled|label|selected|value],q[cite],samp,select[disabled|multiple|name|size],small,' +
		'textarea[cols|rows|disabled|name|readonly],tt,var,big',

	extended_valid_elements:
		'img[*],time[class|datetime|contenteditable|onselectstart|unselectable|oncontrolselect]',

	//white list the removeformat elements we want to support
	formats: {
		alignleft: [
			{
				selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
				styles: { textAlign: 'left' },
			},
			{ selector: 'img.confluence-embedded-image', classes: 'image-left' },
			{ selector: 'img,table', collapsed: false, styles: { float: 'left' } },
		],

		aligncenter: [
			{
				selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
				styles: { textAlign: 'center' },
			},
			//{selector : 'img', collapsed : false, styles : {display : 'block', marginLeft : 'auto', marginRight : 'auto'}},
			{ selector: 'img.confluence-embedded-image', classes: 'image-center' },
			{
				selector: 'table',
				collapsed: false,
				styles: { marginLeft: 'auto', marginRight: 'auto' },
			},
		],

		alignright: [
			{
				selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
				styles: { textAlign: 'right' },
			},
			{ selector: 'img.confluence-embedded-image', classes: 'image-right' },
			{ selector: 'img,table', collapsed: false, styles: { float: 'right' } },
		],

		// ATLASSIAN: prefer semantic tag over inline style
		underline: [
			{ inline: 'u', remove: 'all' },
			{ inline: 'span', styles: { textDecoration: 'underline' }, exact: true },
		],

		// ATLASSIAN: prefer semantic tag over inline style
		strikethrough: [{ inline: 's', remove: 'all' }],

		removeformat: [
			{
				selector: 'h1,h2,h3,h4,h5,h6,pre',
				remove: 'all',
				split: true,
				expand: false,
				block_expand: true,
				deep: true,
				block: '*',
			},
			{
				selector:
					'address,article,b,big,blockquote,center,cite,code,date,dd,del,dfn,dl,dt,em,embed,font,footer,' +
					'header,hgroup,i,ins,kbd,link,menu,nav,object,param,q,s,samp,script,' +
					'section,small,strike,strong,style,sub,sup,time,tt,u,var',
				remove: 'all',
				split: true,
				expand: false,
				block_expand: true,
				deep: true,
			},
			{
				selector: 'span',
				attributes: ['style', 'class'],
				remove: 'empty',
				split: true,
				expand: false,
				deep: true,
			}, // colored text
			{
				selector: 'table',
				attributes: ['cellpadding', 'cellspacing', 'border'],
				split: false,
				expand: false,
				deep: true,
			},
			{
				selector: '*',
				attributes: ['style', 'color', 'bgcolor', 'title', 'lang'],
				split: false,
				expand: false,
				deep: true,
			},
		],
	},

	forced_root_block: 'p',
	force_p_newlines: true, // default
	force_br_newlines: false, // default
	padd_empty_with_br: true,
	allow_unsafe_link_target: true, // Overridden since we don't know what impact it might have on the server
	paste_remove_styles_if_webkit: false, // addresses WEBKIT bug, see https://www.tinymce.com/docs/plugins/paste/#paste_remove_styles_if_webkit

	// CONFDEV-50517: This was removed since 4.x doesn't resolve strings to event handlers. This is now called by tiny through settings.init_instance_callback
	//oninit: 'AJS.Rte.BootstrapManager.onInit',

	/**
	 * Disable resize controls on tables and images in firefox. They render out of place when margins are applied to the table.
	 */
	object_resizing: false,
};

/**
 * This feeds the translations passed from React component (withTranslations decorator) into window.tinymce.
 * @param {string} locale  Locale, e.g. en-GB
 * @param {Map} translations Key-value map with strings in local language.
 */
const initialiseTranslations = (locale, translations) => {
	if (!locale) {
		if (process.env.NODE_ENV !== 'production') {
			console.warn(
				'tinymce-bootstrap.initialiseTranslations()',
				'locale=',
				locale,
				'no translations provided (',
				translations,
				')',
			);
		}
		return;
	}

	// This is inline comments in fallback mode - no translations provided.
	if (typeof translations === 'undefined') {
		// Make sure TinyMCE won't be trying to download the language pack.
		window.tinymce.ScriptLoader.markDone(`${window.tinymce.baseURL}/langs/${locale}.js`);

		// Do not proceed - there are no strings to pass to i18n.
		return;
	}

	// helper to make tool-tips mac friendly
	const ctrlTrans = new RegExp(`${translations['keyboard.shortcut.ctrl']}\+`, 'g');
	const shiftTrans = new RegExp(`${translations['keyboard.shortcut.shift']}\+`, 'g');
	const altTrans = new RegExp(`${translations['keyboard.shortcut.alt']}\+`, 'g');
	/**
	 * Search and convert window keys into mac keys
	 * @param {string} str text to be replaced
	 * @returns {string} text after replacement
	 */
	const replaceForMac = (str) => {
		return str
			.replace(ctrlTrans, '\u2318')
			.replace(shiftTrans, '\u21E7')
			.replace(altTrans, translations['keyboard.shortcut.option']);
	};

	const editorTranslations = {};
	Object.entries(translations).forEach(([key, value]) => {
		if (window.tinymce.Env.mac) {
			value = replaceForMac(value);
		}
		editorTranslations[key] = value;
	});
	window.tinymce.EditorManager.addI18n(locale, editorTranslations);

	// Mapping for plugins that still live in Confluence core.
	const oldPluginsTranslations = {
		// confluence/confluence-plugins/confluence-editor-plugins/confluence-editor/src/main/resources/tinymce3/plugins/propertypanel/js/property-panel-links.js
		'propertypanel.links_goto': 'tinymce.propertypanel.links.goto',
		'propertypanel.links_edit': 'tinymce.propertypanel.links.edit',
		'propertypanel.links_unlink': 'tinymce.propertypanel.links.unlink',
		'propertypanel.links_goto_disabled_tooltip':
			'tinymce.propertypanel.links.goto.disabled.tooltip',
		'propertypanel.links_edit_tooltip': 'tinymce.propertypanel.links.edit.tooltip',
		'propertypanel.links_unlink_tooltip': 'tinymce.propertypanel.links.unlink.tooltip',

		// confluence/confluence-plugins/confluence-editor-plugins/confluence-editor/src/main/resources/tinymce3/plugins/propertypanel/js/property-panel-images.js
		'propertypanel.images_small': 'tinymce.propertypanel.images.small',
		'propertypanel.images_medium': 'tinymce.propertypanel.images.medium',
		'propertypanel.images_large': 'tinymce.propertypanel.images.large',
		'propertypanel.images_original': 'tinymce.propertypanel.images.original',
		'propertypanel.images_border': 'tinymce.propertypanel.images.border',
		'propertypanel.images_link_create': 'tinymce.propertypanel.images.link.create',
		'propertypanel.images_link_edit': 'tinymce.propertypanel.images.link.edit',
		'propertypanel.images_link_remove': 'tinymce.propertypanel.images.link.remove',
		'propertypanel.images_link_create_tooltip': 'tinymce.propertypanel.images.link.create.tooltip',
		'propertypanel.images_link_edit_tooltip': 'tinymce.propertypanel.images.link.edit.tooltip',
		'propertypanel.images_link_remove_tooltip': 'tinymce.propertypanel.images.link.remove.tooltip',
		'propertypanel.images_sizing_tooltip': 'tinymce.propertypanel.images.sizing.tooltip',
		'propertypanel.images_small_tooltip': 'tinymce.propertypanel.images.small.tooltip',
		'propertypanel.images_medium_tooltip': 'tinymce.propertypanel.images.medium.tooltip',
		'propertypanel.images_large_tooltip': 'tinymce.propertypanel.images.large.tooltip',
		'propertypanel.images_original_tooltip': 'tinymce.propertypanel.images.original.tooltip',
		'propertypanel.images_thumbnail_tooltip': 'tinymce.propertypanel.images.thumbnail.tooltip',
		'propertypanel.images_border_tooltip': 'tinymce.propertypanel.images.border.tooltip',
	};
	let key;
	for (key in oldPluginsTranslations) {
		window.tinymce.EditorManager.i18n.data[`${locale}.${key}`] = window.tinymce.translate(
			oldPluginsTranslations[key],
		);
	}

	// Make sure TinyMCE won't be trying to download the language pack.
	window.tinymce.ScriptLoader.markDone(`${window.tinymce.baseURL}/langs/${locale}.js`);

	// fix up the the tooltips on the toolbar
	if (window.tinymce.Env.mac) {
		Array.prototype.forEach.call(
			window.document.querySelectorAll(
				'#rte-toolbar .toolbar-item, #rte-toolbar .dropdown-item, #rte-savebar .aui-button',
			),
			(el) => {
				const title = el.getAttribute('title');
				const dataTooltip = el.dataset.tooltip;
				if (title) {
					el.setAttribute('title', replaceForMac(title));
				}
				if (dataTooltip) {
					el.dataset.tooltip = replaceForMac(dataTooltip);
				}
			},
		);
	}
};

/**
 * Resolve plugins to be initialized with editor
 *
 * @param {string} originalPlugins The original plugins before initialization
 * @param {Array} includePlugins List of additional plugins to be included
 * @param {Array} excludePlugins List of plugins to be excluded, plugins in this list will override plugins from the parameter before
 */
function resolvePlugins(originalPlugins, includePlugins, excludePlugins) {
	let editorPlugins = originalPlugins.split(',');
	includePlugins.forEach((plugin) => {
		if (editorPlugins.indexOf(plugin) < 0) {
			editorPlugins.push(plugin);
		}
	});
	editorPlugins = editorPlugins.filter((plugin) => excludePlugins.indexOf(plugin) < 0);
	return editorPlugins.join(',');
}

const BootstrapManager = {
	/**
	 * Non interface methods & variables
	 */
	_tinyMceHasInit: false,
	_beforeInitCallbacks: [],
	_tinymcePluginInits: [],
	/**
	 * The settings used (or to be used) for initialising
	 */
	_settings: null,

	/**
	 * @return true when TinyMCE has initialised. Note that this does not
	 * mean there is an Editor active, it simply means that TinyMCE is in
	 * a state where it could activate an editor.
	 */
	isInitComplete: () => {
		return BootstrapManager._tinyMceHasInit;
	},

	/**
	 * @return true if there is currently an Editor active.
	 */
	isEditorActive: () => {
		return !!(BootstrapManager.isInitComplete() && window.tinymce && window.tinymce.activeEditor);
	},

	onInit: () => {
		BootstrapManager._tinyMceHasInit = true;
		const ed = window.AJS.Rte.getEditor();

		/* CONFDEV-43719: we want to prevent google form translating content inside editor */
		ed.getBody().classList.add('notranslate');

		// At this point, the editor may not be fully initialised yet
		// Please use "rte-ready" to bind editor initialisation related events
		// "init.rte" will be eventually deprecated
		trigger('init.rte', { editor: ed });

		// get editor-auto-focus parameter.
		// This parameter is optional and defaults to true, unless it is explicitly set to false.
		const autoFocus = getOverride('editor-auto-focus') !== false;
		if (autoFocus) {
			const inputTitle = window.document.getElementById('content-title');
			if (inputTitle && inputTitle.value === '') {
				// CONFDEV-57498: in case SPA create page, title is empty => focus on title
				inputTitle.focus();
			} else {
				window.AJS.Rte.editorFocus(ed);
			}
		} else {
			if (process.env.NODE_ENV !== 'production') {
				console.log('Bootstrap: editor-auto-focus=false. Do not focus the editor automatically.');
			}
		}
	},

	/**
	 * Adds a callback that will be executed after this editor instance has been initialised.
	 * @param callback
	 * @deprecated since 4.1 use bind("init.rte", callback);
	 */
	addOnInitCallback: (callback) => {
		if (typeof callback === 'function') {
			if (BootstrapManager._tinyMceHasInit) {
				callback();
			} else {
				bind('init.rte', callback);
			}
		} else {
			throw new Error(
				`Attempt made to register an oninit callback that is not a function. Received: ${callback}`,
			);
		}
	},

	/**
	 * Add a function to be called immediately before AJS.Rte.BootstrapManager.initialise. A typical usage would
	 * be to refresh any settings that may have changed or become available since AJS.Rte.BootstrapManager.preInitialise.
	 *
	 * @param func the function to be called before initialise. This function can take a single parameter which will
	 * be the TinyMCE settings object.
	 */
	addBeforeInitCallback: (func) => {
		BootstrapManager._beforeInitCallbacks.push(func);
	},

	addTinyMcePluginInit: (func) => {
		BootstrapManager._tinymcePluginInits.push(func);
	},

	/**
	 * This prepares for TinyMCE initialisation although it does not instigate the initialisation.
	 *
	 * @param settings - tinymce settings to be used when initialise is later called.
	 * @param settings.locale {string}    Passed to initialiseTranslations.
	 * @param settings.messages {object} Passed to initialiseTranslations.
	 *
	 */
	preInitialise: (settings) => {
		BootstrapManager._settings = Object.assign({}, defaultSettings, settings);
	},

	/**
	 * Initialises the TinyMCE editor. The HTML for the Editor must be available at the point
	 * you call this function.
	 *
	 * @param options {object}
	 * @param options.plugins {Array} List of additional plugins to load the editor with
	 * @param options.excludePlugins {Array} List of loaded plugins to be excluded from the editor
	 * @param options.onInitialise {Function} Callback to be executed right after TinyMCE was initialised
	 */
	initialise: (options) => {
		debugTime('confluence.editor.tinymce');

		if (!BootstrapManager._settings) {
			if (process.env.NODE_ENV !== 'production') {
				console.log('Bootstrap:initialise: No settings found. Has preInitialise been called?');
			}
			return;
		}

		options = Object.assign({}, options); // copy to prevent reference mutation
		const settings = Object.assign({}, BootstrapManager._settings); // copy to prevent reference mutation

		window.AJS.Rte.isQuickEdit = options.isQuickEdit || false;

		if (process.env.NODE_ENV !== 'production') {
			console.debug(
				`Bootstrap:initialise: Initialising TinyMce version ${window.tinymce.majorVersion}.${window.tinymce.minorVersion}`,
			);
		}

		// Some settings are taken from the DOM for the Editor. This isn't necessary available until this point.
		// (You can't initialise without the DOM being available.)
		// This is really an artifact of the fact that we either use the Editor from a full page load, or we
		// load it dynamically after the initial page load. It is assumed that in the future the Editor will
		// always be dynamically initiated which means this mechanism can be replaced with something more
		// appropriate.
		BootstrapManager._beforeInitCallbacks.forEach((func) => {
			func(settings);
		});

		trigger('rte-pre-resolveplugins');

		// plugin point for tinymce plugins to configure settings
		for (let i = 0, ii = BootstrapManager._tinymcePluginInits.length; i < ii; i++) {
			if (typeof BootstrapManager._tinymcePluginInits[i] === 'function') {
				BootstrapManager._tinymcePluginInits[i](settings);
			}
		}

		initialiseTranslations(settings.locale, settings.messages);

		// configure toolbar
		configureToolbar('toolbar', options.toolbar);

		const pluginsNotImplementedYet = [
			// 'auiwindowmanager',         //Implemented.
			// 'table',
			// 'emotions',
			// 'confluence',
			// 'macroplaceholder',
			// 'customtoolbar',
			// 'insertwikimarkup',
			// 'propertypanel',            //Implemented.
			// 'keyboardshortcuts',        //Copied.  KeyboardShortcutsObject not defined.
			// 'confmonospace',
			// 'confcharmap',           //Implemented.  TinyMCEPopup
			// 'lists',
			// 'conflist',
			// 'tasklist',
			// 'aePaste',
			// 'cursorTarget',          //Implemented
			'confluencecleanupplugin',
			'confluencepastetableplugin',
			'confluencepastemacroplugin',
			// 'pagelayoutplugin',
			// 'iosediting',
			// 'autoconvert',
			'confluencelist',
			// 'deletecommand',
			// 'draggable',
			// 'confluencesortabletablesplugin',
			// 'textplaceholders',
			// 'linkbrowser',              //Implemented. This is available in editor-plugins.
			// 'insertfiles',
			// 'tableAnalytics',
			// 'tableContentWrapper',
			// 'autocomplete',
			// 'autocompletelink',
			// 'autocompletemedia',
			// 'searchreplace',
			// 'confluencedrafts',
			// 'confluenceimagedialog',
			// 'autocompletemacro',
			// 'confluencemacrobrowser',
			// 'flextofullsize',
			// 'referrer',
			// 'confluenceversioncomment',
			//'confluencenotifywatchers',
			//'fabricmention'
		];
		options.excludePlugins =
			options.excludePlugins instanceof Array
				? pluginsNotImplementedYet.concat(options.excludePlugins)
				: pluginsNotImplementedYet; // TODO: remove this after all plugins are ready.

		settings.plugins = resolvePlugins(
			settings.plugins,
			options.plugins || [],
			options.excludePlugins,
		);

		/**
		 * CONFDEV-37087
		 * provide ability to setup some handlers right after editor is initialised
		 * can not rely on postInitialise, b/c it is too late, some events had been triggered before postInitialise (e.g onLoad)
		 */
		settings.setup = options.onInitialise;

		if (getOverride('content-type') === 'page' || getOverride('content-type') === 'blogpost') {
			/*
			 * CONFCLOUD-57270 - disable Grammarly.  Grammarly requires the Attr data-gram="false" to disable
			 * Tinymce has a new feature to decorate the iframe custom Attrs.
			 * Its unique in that the ATTRS will be added to the elements before they are appended to the DOM, which is a requirement of grammarly to disble.
			 *
			 *    settings = {
			 *      'iframe_attrs' : { },
			 *    }
			 */
			settings.iframe_attrs = {
				'data-gramm': false,
			};
		}

		// CONFDEV-50517: Changed so "rte-ready" is fired on editor init
		settings.init_instance_callback = function (editor) {
			//make sure AJS.Rte.getEditor is defined so other modules from Confluence can use it
			exportAjsRteAsGlobal();

			// Makes confluence toolbar elements part of the editor UI so blur and selection restore works properly
			//TODO: Refactor this function out as a JQuery helper - it's not obvious what it's doing.
			//TODO: This is the same as: '$(elm).parents('#rte-toolbar').length > 0;
			window.tinymce.FocusManager.isEditorUIElement = function (elm) {
				let currentElement = elm;
				let isChildElementOfRteToolbar = false;
				// Browser DOM upwards to find out if this is child element of #rte-toolbar. Stops at <html> as that has no parrent.
				while (currentElement) {
					if (currentElement.id === 'rte-toolbar') {
						isChildElementOfRteToolbar = true;
						break;
					}
					currentElement = currentElement.parentElement;
				}
				return isChildElementOfRteToolbar;
			};

			// Add custom classes could probably be made nicer using the content_css option
			try {
				const cssTags = settings.contentCssTags;
				const doc = editor.getDoc();

				// Setup support for dark mode inside the TinyMCE editor iframe
				const onThemeUpdated = (iframeThemeInfo) => applyThemeInsideIframe(iframeThemeInfo, doc);
				setupThemeInsideIframe(onThemeUpdated);

				const head = doc.querySelectorAll('head');
				if (typeof head[0] === 'undefined') {
					throw new Error('No editor <head>');
				}
				head[0].innerHTML = cssTags + head[0].innerHTML; // Prepend styles to the head.
			} catch (ex) {
				// Crash silently.
			}

			window.AJS.Rte.BootstrapManager.onInit(editor);

			// so we don't trigger drafts due to macro manipulation
			AtlassianEditorContent.editorResetContentChanged();

			/*
			 * Add a tab index to the Editor. The Save button will be given 101
			 */
			window.AJS.Rte.getEditorFrame().setAttribute('tabindex', 100);

			if (window.tinymce.isIE) {
				window.AJS.Rte.getEditorFrame().setAttribute('hidefocus', 'hidefocus');
			}

			// At this point, the editor is ready to be used.
			// Please use rte-ready to bind editor initialisation related events
			debugTimeEnd('confluence.editor.tinymce');

			trigger('rte-ready');
		};

		/**
		 * tinyMCE initialisation
		 */
		window.tinymce.init(settings);
	},
};

export default BootstrapManager;
